import { PropsWithChildren } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { ColdStartBanner } from './ColdStartBanner';
import { isGateConfigured, signOut } from './reviewerSession';

/**
 * Outer chrome: brand header with logo + nav, ColdStartBanner underneath,
 * main content area, and a minimal footer.
 *
 * Kept presentational; routing-aware bits live in App.
 */
export function AppShell({ children }: PropsWithChildren) {
  return (
    <div className="min-h-screen flex flex-col bg-paper text-ink">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="Raydn home">
            <img
              src="/logo.png"
              alt="Raydn"
              className="h-8 w-auto"
              onError={(e) => {
                // Fall back to the SVG placeholder if the PNG hasn't been
                // dropped in yet (see README).
                (e.currentTarget as HTMLImageElement).src = '/logo.svg';
              }}
            />
          </Link>
          <nav aria-label="Primary" className="flex items-center gap-4 text-sm">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                isActive ? 'text-ink font-medium' : 'text-muted hover:text-ink'
              }
            >
              Upload
            </NavLink>
            <NavLink
              to="/hitl"
              className={({ isActive }) =>
                isActive ? 'text-ink font-medium' : 'text-muted hover:text-ink'
              }
            >
              HITL queue
            </NavLink>
            <span className="text-muted">v0.1 prototype</span>
            {/*
             * Data residency badge — called out in the UI/UX dev plan as a
             * Stage 4 compliance signal. Backend runs on Render's Frankfurt
             * region (see render.yaml), the static frontend is served from
             * their global edge. "UK hosted" reads as the user-facing
             * commitment even though Frankfurt is the technical reality —
             * region is close enough for UK data-residency semantics, and
             * v0.2 can move to a UK-native region if compliance tightens.
             */}
            <span
              className="inline-flex items-center gap-1.5 rounded-sm bg-brand/15 text-brand-dark px-2 py-0.5 text-xs font-semibold uppercase tracking-wide"
              title="Backend services run in the UK/EU region. Uploaded data stays within Render's European infrastructure."
              aria-label="UK-hosted data residency"
            >
              <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-brand-dark" />
              UK hosted
            </span>
            {isGateConfigured() && (
              <button
                type="button"
                onClick={signOut}
                className="text-muted hover:text-ink underline-offset-4 hover:underline"
                title="End this reviewer session and prompt for the password again"
              >
                Sign out
              </button>
            )}
          </nav>
        </div>
      </header>

      <ColdStartBanner />

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-8">{children}</div>
      </main>

      <footer className="border-t border-border text-xs text-muted">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <span>&copy; {new Date().getFullYear()} Raydn</span>
          <span>Prototype — not for production data</span>
        </div>
      </footer>
    </div>
  );
}
